import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { 
  ArrowLeft, HelpCircle, Mail, Clock, MessageSquare, 
  Users, CreditCard, Bell, Trash2, XCircle, ChevronDown,
  ChevronUp, FileText, Shield, ExternalLink, AlertTriangle,
  CheckCircle, Copy, Check
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getApiUrl = () => {
  const h = window.location.hostname;
  return (h === 'localhost' || h.includes('preview.emergentagent.com')) 
    ? (BACKEND_URL || '') 
    : window.location.origin;
};
const API = `${getApiUrl()}/api`;

const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <motion.div 
    className="rounded-xl border border-border overflow-hidden"
    initial={false}
  >
    <button
      onClick={onClick}
      className="w-full flex items-center justify-between p-4 bg-card hover:bg-muted/50 transition-colors text-left"
    >
      <span className="font-medium pr-4">{question}</span>
      {isOpen ? <ChevronUp className="w-5 h-5 shrink-0" /> : <ChevronDown className="w-5 h-5 shrink-0" />}
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <div className="p-4 pt-0 text-muted-foreground bg-card">
            {answer}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </motion.div>
);

const Support = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [openFAQ, setOpenFAQ] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const faqs = [
    {
      id: 1,
      icon: <Users className="w-5 h-5 text-violet-500" />,
      question: "How do I create a group?",
      answer: (
        <div className="space-y-2">
          <p>Creating a group on SharePay is easy:</p>
          <ol className="list-decimal list-inside space-y-1 ml-2">
            <li>Go to your <strong>Dashboard</strong></li>
            <li>Tap the <strong>"+ Create Group"</strong> button</li>
            <li>Enter a group name (e.g., "Flat Rent", "Goa Trip 2026")</li>
            <li>Set your contribution goal and frequency (daily/weekly/monthly)</li>
            <li>Add your UPI ID for receiving payments</li>
            <li>Share the group invite link with friends!</li>
          </ol>
          <p className="mt-2 text-sm">As the group admin, you can manage members, send reminders, and track all contributions.</p>
        </div>
      )
    },
    {
      id: 2,
      icon: <CreditCard className="w-5 h-5 text-green-500" />,
      question: "How do I send and receive money?",
      answer: (
        <div className="space-y-2">
          <p><strong>To send money (contribute):</strong></p>
          <ol className="list-decimal list-inside space-y-1 ml-2">
            <li>Open the group you want to contribute to</li>
            <li>Tap <strong>"Contribute"</strong> or <strong>"Pay Now"</strong></li>
            <li>Enter the amount</li>
            <li>You'll be redirected to your UPI app to complete payment</li>
            <li>After payment, mark it as "Paid" in the app</li>
          </ol>
          <p className="mt-3"><strong>To receive money:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Ensure your UPI ID is added to your profile</li>
            <li>When someone contributes, the payment goes directly to your UPI</li>
            <li>Track all received payments in the group's contribution history</li>
          </ul>
        </div>
      )
    },
    {
      id: 3,
      icon: <Bell className="w-5 h-5 text-orange-500" />,
      question: "How do reminder settings work?",
      answer: (
        <div className="space-y-2">
          <p>SharePay's smart reminder system helps ensure timely payments:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><strong>Automatic Reminders:</strong> Sent based on due dates (1 day before, on due date, overdue)</li>
            <li><strong>Manual Reminders:</strong> Group admins can send reminders anytime</li>
            <li><strong>Escalation:</strong> Gentle → Firm → Overdue notifications</li>
          </ul>
          <p className="mt-2">To customize your reminder preferences:</p>
          <ol className="list-decimal list-inside space-y-1 ml-2">
            <li>Go to <strong>Profile → Settings</strong></li>
            <li>Tap <strong>"Reminder Settings"</strong></li>
            <li>Choose when to receive reminders</li>
            <li>Enable/disable push notifications</li>
          </ol>
        </div>
      )
    },
    {
      id: 4,
      icon: <AlertTriangle className="w-5 h-5 text-red-500" />,
      question: "What if someone doesn't pay?",
      answer: (
        <div className="space-y-2">
          <p>If a group member doesn't pay on time:</p>
          <ol className="list-decimal list-inside space-y-1 ml-2">
            <li><strong>Automatic Reminders:</strong> The system will send escalating reminders</li>
            <li><strong>Overdue Status:</strong> Their contribution will be marked as "Overdue"</li>
            <li><strong>Group Admin Actions:</strong> Admins can send manual reminders</li>
            <li><strong>Pay for Me:</strong> Another member can cover their share temporarily</li>
          </ol>
          <p className="mt-2 p-3 bg-orange-100 dark:bg-orange-900/30 rounded-lg text-sm">
            <strong>Note:</strong> SharePay is a coordination tool. We cannot force payments or guarantee that members will pay. Any disputes should be resolved directly between group members.
          </p>
        </div>
      )
    },
    {
      id: 5,
      icon: <Trash2 className="w-5 h-5 text-gray-500" />,
      question: "How do I cancel or delete my account?",
      answer: (
        <div className="space-y-2">
          <p>To delete your SharePay account:</p>
          <ol className="list-decimal list-inside space-y-1 ml-2">
            <li>Ensure all your balances are settled</li>
            <li>Leave or transfer ownership of any groups you admin</li>
            <li>Go to <strong>Support → Delete Account</strong> (below)</li>
            <li>Confirm deletion by typing the required text</li>
          </ol>
          <p className="mt-2 p-3 bg-red-100 dark:bg-red-900/30 rounded-lg text-sm">
            <strong>Warning:</strong> Account deletion is <strong>permanent and irreversible</strong>. All your data, groups, and transaction history will be permanently deleted within 30 days.
          </p>
          {user && (
            <Button 
              onClick={() => setShowDeleteDialog(true)}
              variant="destructive"
              className="mt-3"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Delete My Account
            </Button>
          )}
        </div>
      )
    }
  ];

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE MY ACCOUNT') {
      toast.error('Please type "DELETE MY ACCOUNT" to confirm');
      return;
    }

    setIsDeleting(true);
    try {
      await axios.delete(`${API}/users/${user.id}/delete-account`);
      toast.success('Your account has been deleted');
      setShowConfirmDialog(false);
      setShowDeleteDialog(false);
      // Log out user
      if (onLogout) {
        onLogout();
      }
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50 dark:from-slate-950 dark:via-slate-900 dark:to-violet-950">
      <Helmet>
        <title>Help & Support | SharePay India - Contact Us</title>
        <meta name="description" content="Get help with SharePay India - FAQs, contact support via Instagram DM @sharepay.today, account help and troubleshooting." />
        <link rel="canonical" href="https://sharepay.today/support" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      {/* Header */}
      <div className="bg-card/80 backdrop-blur-xl border-b border-border sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <h1 className="font-bold gradient-text">Help & Support</h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          {/* Header Section */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center mx-auto mb-4">
              <HelpCircle className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl font-bold mb-2">How can we help?</h1>
            <p className="text-muted-foreground">Find answers to common questions or contact our support team</p>
          </div>

          {/* Contact Card - Instagram DM Primary */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 p-6 text-white"
          >
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
                  <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Instant Support on Instagram</h3>
                  <p className="opacity-90 text-sm">DM us @sharepay.today for fastest response!</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-center px-4">
                  <Clock className="w-5 h-5 mx-auto mb-1 opacity-80" />
                  <p className="text-sm opacity-80">~5 min response</p>
                </div>
                <Button 
                  onClick={() => {
                    navigator.clipboard.writeText('@sharepay.today');
                    toast.success('Instagram handle copied! Open Instagram app and search @sharepay.today');
                  }}
                  className="bg-white text-pink-600 hover:bg-white/90 rounded-full font-semibold"
                  data-testid="instagram-copy-btn"
                >
                  <Copy className="w-4 h-4 mr-2" /> Copy Handle
                </Button>
              </div>
            </div>
            <p className="text-white/70 text-xs mt-4 text-center">
              Open your Instagram app and search for @sharepay.today to DM us
            </p>
          </motion.div>

          {/* Email Support (Secondary) */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="rounded-2xl bg-card border border-border p-6"
          >
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                  <Mail className="w-6 h-6 text-violet-600" />
                </div>
                <div>
                  <h3 className="font-semibold">Email Support</h3>
                  <p className="text-muted-foreground text-sm">support@sharepay.today</p>
                </div>
              </div>
              <Button 
                onClick={() => window.location.href = 'mailto:support@sharepay.today'}
                variant="outline"
                className="rounded-full"
              >
                <Mail className="w-4 h-4 mr-2" /> Send Email
              </Button>
            </div>
          </motion.div>

          {/* FAQs */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-violet-500" /> Frequently Asked Questions
            </h2>
            
            <div className="space-y-3">
              {faqs.map((faq) => (
                <FAQItem
                  key={faq.id}
                  question={
                    <span className="flex items-center gap-2">
                      {faq.icon} {faq.question}
                    </span>
                  }
                  answer={faq.answer}
                  isOpen={openFAQ === faq.id}
                  onClick={() => setOpenFAQ(openFAQ === faq.id ? null : faq.id)}
                />
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="rounded-2xl bg-card border border-border p-6">
            <h3 className="font-semibold mb-4">Quick Links</h3>
            <div className="grid grid-cols-2 gap-3">
              <Button 
                variant="outline" 
                onClick={() => navigate('/privacy')}
                className="justify-start rounded-xl"
              >
                <Shield className="w-4 h-4 mr-2" /> Privacy Policy
              </Button>
              <Button 
                variant="outline" 
                onClick={() => navigate('/terms')}
                className="justify-start rounded-xl"
              >
                <FileText className="w-4 h-4 mr-2" /> Terms of Service
              </Button>
            </div>
          </div>

          {/* Delete Account Section */}
          {user && (
            <div className="rounded-2xl bg-card border border-red-200 dark:border-red-900 p-6">
              <h3 className="font-semibold mb-2 flex items-center gap-2 text-red-600 dark:text-red-400">
                <Trash2 className="w-5 h-5" /> Delete Account
              </h3>
              <p className="text-muted-foreground text-sm mb-4">
                Permanently delete your account and all associated data. This action cannot be undone.
              </p>
              <Button 
                onClick={() => setShowDeleteDialog(true)}
                variant="destructive"
                className="rounded-full"
                data-testid="delete-account-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Delete My Account
              </Button>
            </div>
          )}
        </motion.div>
      </div>

      {/* Delete Account Dialog - Step 1 */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" /> Delete Account?
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete your account? This action is permanent and cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800">
              <p className="text-sm text-red-700 dark:text-red-300 font-medium mb-2">
                What will be deleted:
              </p>
              <ul className="text-sm text-red-600 dark:text-red-400 space-y-1">
                <li>• All your personal information</li>
                <li>• Your groups and memberships</li>
                <li>• All transaction history</li>
                <li>• All contribution records</li>
                <li>• Your profile and settings</li>
              </ul>
            </div>
            
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => setShowDeleteDialog(false)}
                className="flex-1 rounded-full"
              >
                Cancel
              </Button>
              <Button 
                variant="destructive"
                onClick={() => { setShowDeleteDialog(false); setShowConfirmDialog(true); }}
                className="flex-1 rounded-full"
                data-testid="continue-delete-btn"
              >
                Continue
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Account Dialog - Step 2 (Confirmation) */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="w-5 h-5" /> Final Confirmation
            </DialogTitle>
            <DialogDescription>
              This is your last chance to cancel. Type <strong>"DELETE MY ACCOUNT"</strong> below to confirm.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl border border-yellow-200 dark:border-yellow-800">
              <p className="text-sm text-yellow-700 dark:text-yellow-300 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                <strong>Warning:</strong> All data will be lost permanently!
              </p>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">
                Type "DELETE MY ACCOUNT" to confirm:
              </label>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="DELETE MY ACCOUNT"
                className="rounded-xl"
                data-testid="delete-confirm-input"
              />
            </div>
            
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => { setShowConfirmDialog(false); setDeleteConfirmText(''); }}
                className="flex-1 rounded-full"
              >
                Cancel
              </Button>
              <Button 
                variant="destructive"
                onClick={handleDeleteAccount}
                disabled={deleteConfirmText !== 'DELETE MY ACCOUNT' || isDeleting}
                className="flex-1 rounded-full"
                data-testid="final-delete-btn"
              >
                {isDeleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" /> Delete Forever
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Support;
